import { Editor, useEditor as useTiptapEditor } from "@tiptap/react"
import classNames from "classnames"
import { useEffect, useMemo } from "react"
import { getExtensions } from "./getExtensions"

type UseEditor = {
  ariaLabelledBy: string
  disabled: boolean
  defaultValue?: string
  editorClassName?: string
  error?: string
  isFixedHeight?: boolean
  onFocus?: (event: FocusEvent) => void
  onBlur?: (event: FocusEvent) => void
  onChange?: (content: string) => void
  placeholder?: string
}

export const useEditor = ({
  ariaLabelledBy,
  disabled,
  defaultValue,
  editorClassName,
  error,
  isFixedHeight,
  onBlur,
  onChange,
  onFocus,
  placeholder,
}: UseEditor): Editor | null => {
  const baseClass = classNames(
    "space-y-2",
    "focus-visible:outline-none",
    "focus-within:border-primary",
    "px-4",
    "py-3",
    "max-h-32",
    "overflow-y-auto",
    {
      ["h-32"]: isFixedHeight,
    },
    editorClassName,
  )

  const baseAttributes = useMemo(
    () => ({
      class: baseClass,
      "aria-labelledby": ariaLabelledBy,
      role: "textbox",
    }),
    [baseClass, ariaLabelledBy],
  )

  const extensions = getExtensions(placeholder)

  const editor = useTiptapEditor({
    editable: !disabled,
    extensions,
    content: defaultValue,
    editorProps: {
      transformPastedHTML(html) {
        // When copying content from google docs with tabs
        // they get ignored by tiptap, which breaks the formatting expectations
        // This replaces the tabs with nbsp which gets rendered correctly
        // 1 tab = 8 nbsp
        const tabToNbspGoogleDocs = "&nbsp;".repeat(8)
        return html.replace(/\t/g, tabToNbspGoogleDocs)
      },
      attributes: { ...baseAttributes },
    },
    onBlur: ({ event }) => {
      onBlur?.(event)
    },
    onUpdate: ({ editor }) => {
      let editorContent = editor.getHTML()
      if (editor.getText() === "") {
        editorContent = ""
      }
      onChange?.(editorContent)
    },
    onFocus: ({ event }) => {
      onFocus?.(event)
    },
  })

  // Error styles
  useEffect(() => {
    if (editor) {
      editor.setOptions({
        editorProps: {
          attributes: {
            ...baseAttributes,
            class: classNames(baseClass, { "border-error-base focus:border-error-base": error }),
          },
        },
      })
    }
  }, [baseAttributes, baseClass, editor, error])

  useEffect(() => {
    if (editor) {
      editor.setOptions({ editable: !disabled })
    }
  }, [baseAttributes, baseClass, editor, disabled])

  return editor
}
